import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

interface Member {
  id: string;
  username: string;
  nickname: string | null;
  phone: string | null;
  avatar_url: string | null;
  level: string;
}

interface SetReferrerModalProps {
  member: Member;
  onClose: () => void;
  onSuccess: () => void;
}

const levelColor: Record<string, string> = {
  '会员': 'bg-gray-100 text-gray-500',
  '销售员': 'bg-emerald-50 text-emerald-600',
  '批发': 'bg-sky-50 text-sky-600',
  '主管': 'bg-amber-50 text-amber-600',
  '经理': 'bg-orange-50 text-orange-600',
  '总监': 'bg-rose-50 text-rose-600',
  '董事': 'bg-violet-50 text-violet-600',
  '总裁': 'bg-red-50 text-red-600',
};

const SetReferrerModal = ({ member, onClose, onSuccess }: SetReferrerModalProps) => {
  const [search, setSearch] = useState('');
  const [candidates, setCandidates] = useState<Member[]>([]);
  const [currentReferrer, setCurrentReferrer] = useState<Member | null>(null);
  const [selected, setSelected] = useState<Member | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [loadingCurrent, setLoadingCurrent] = useState(true);

  // 拉取当前上级
  useEffect(() => {
    const fetchCurrent = async () => {
      setLoadingCurrent(true);
      const { data: me } = await supabase
        .from('custom_users')
        .select('referrer_id')
        .eq('id', member.id)
        .maybeSingle();

      if (me?.referrer_id) {
        const { data: ref } = await supabase
          .from('custom_users')
          .select('id, username, nickname, phone, avatar_url, level')
          .eq('id', me.referrer_id)
          .maybeSingle();
        setCurrentReferrer(ref as Member ?? null);
      } else {
        setCurrentReferrer(null);
      }
      setLoadingCurrent(false);
    };
    fetchCurrent();
  }, [member.id]);

  // 搜索候选上级（排除自己和自己的下级，防止循环）
  const fetchCandidates = useCallback(async (q: string) => {
    if (!q.trim()) {
      setCandidates([]);
      return;
    }
    const { data } = await supabase
      .from('custom_users')
      .select('id, username, nickname, phone, avatar_url, level')
      .neq('id', member.id)
      .or(`nickname.ilike.%${q}%,username.ilike.%${q}%,phone.ilike.%${q}%`)
      .limit(10);
    setCandidates((data as Member[]) ?? []);
  }, [member.id]);

  useEffect(() => {
    const timer = setTimeout(() => fetchCandidates(search), 300);
    return () => clearTimeout(timer);
  }, [search, fetchCandidates]);

  const handleSave = async () => {
    setSubmitting(true);
    const newReferrerId = selected === null ? null : selected.id;

    const { error } = await supabase
      .from('custom_users')
      .update({ referrer_id: newReferrerId })
      .eq('id', member.id);

    if (!error) {
      // 写入管理日志
      await supabase.from('admin_logs').insert({
        action: '设置上级',
        target_type: '会员',
        target_id: member.id,
        detail: {
          memberName: member.nickname ?? member.username,
          newReferrer: selected ? (selected.nickname ?? selected.username) : '无（清除绑定）',
          prevReferrer: currentReferrer ? (currentReferrer.nickname ?? currentReferrer.username) : '无',
        },
      });
      setSuccess(true);
    }
    setSubmitting(false);
  };

  const avatarChar = (m: Member) => (m.nickname ?? m.username ?? '?')[0].toUpperCase();

  // 是否有改动
  const hasChange = selected !== undefined;
  // selected === null 表示「清除上级」，selected 为 Member 表示「设置新上级」
  const canSave = selected !== undefined && !submitting;

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-50" onClick={() => !submitting && onClose()} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className="bg-white rounded-2xl w-full max-w-md p-6"
          style={{ animation: 'popIn 0.25s cubic-bezier(0.34,1.56,0.64,1)' }}
          onClick={(e) => e.stopPropagation()}
        >
          {success ? (
            /* 成功状态 */
            <div className="flex flex-col items-center py-2">
              <div
                className="w-20 h-20 flex items-center justify-center rounded-full bg-emerald-50 mb-4"
                style={{ animation: 'popIn 0.4s cubic-bezier(0.34,1.56,0.64,1)' }}
              >
                <i className="ri-links-line text-4xl text-emerald-500" />
              </div>
              <h3 className="font-bold text-gray-900 text-lg mb-1">绑定关系已更新！</h3>
              <p className="text-sm text-gray-500 mb-4 text-center">
                <span className="font-medium text-gray-700">{member.nickname ?? member.username}</span>
                {selected
                  ? <> 的上级已设置为 <span className="font-medium text-emerald-600">{selected.nickname ?? selected.username}</span></>
                  : <> 的上级绑定已清除</>
                }
              </p>
              <button
                onClick={() => { onSuccess(); onClose(); }}
                className="w-full bg-emerald-500 text-white py-3 rounded-xl text-sm font-medium cursor-pointer whitespace-nowrap hover:bg-emerald-600 transition-colors"
              >
                完成
              </button>
            </div>
          ) : (
            <>
              {/* 标题 */}
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-full bg-sky-50 flex items-center justify-center flex-shrink-0">
                  <i className="ri-links-line text-sky-500 text-xl" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-base font-bold text-gray-900">设置上级</h3>
                  <p className="text-xs text-gray-400 truncate">
                    为 <span className="text-gray-600 font-medium">{member.nickname ?? member.username}</span> 手动绑定上级关系
                  </p>
                </div>
              </div>

              {/* 当前上级 */}
              <div className="bg-gray-50 rounded-xl px-4 py-3 mb-4">
                <p className="text-xs text-gray-400 mb-2">当前上级</p>
                {loadingCurrent ? (
                  <div className="h-5 w-24 bg-gray-200 rounded animate-pulse" />
                ) : currentReferrer ? (
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-rose-400 to-orange-300 flex items-center justify-center flex-shrink-0 overflow-hidden">
                      {currentReferrer.avatar_url ? (
                        <img src={currentReferrer.avatar_url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-white text-xs font-bold">{avatarChar(currentReferrer)}</span>
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-800">{currentReferrer.nickname ?? currentReferrer.username}</p>
                      <p className="text-xs text-gray-400">{currentReferrer.phone ?? `@${currentReferrer.username}`}</p>
                    </div>
                    <span className={`ml-auto text-xs px-2 py-0.5 rounded-full font-medium ${levelColor[currentReferrer.level] ?? 'bg-gray-100 text-gray-500'}`}>
                      {currentReferrer.level}
                    </span>
                  </div>
                ) : (
                  <p className="text-sm text-gray-400">暂无上级</p>
                )}
              </div>

              {/* 搜索新上级 */}
              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">搜索并选择新上级</label>
                <div className="flex items-center gap-2 border border-gray-200 rounded-xl px-3 py-2.5 focus-within:border-sky-400 transition-colors">
                  <i className="ri-search-line text-gray-400 text-sm flex-shrink-0" />
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => { setSearch(e.target.value); setSelected(undefined as unknown as Member); }}
                    placeholder="输入昵称 / 用户名 / 手机号搜索"
                    className="flex-1 text-sm outline-none placeholder-gray-400"
                  />
                  {search && (
                    <button onClick={() => { setSearch(''); setCandidates([]); }} className="text-gray-300 hover:text-gray-500 cursor-pointer">
                      <i className="ri-close-line text-sm" />
                    </button>
                  )}
                </div>
              </div>

              {/* 候选列表 */}
              {candidates.length > 0 && (
                <div className="border border-gray-100 rounded-xl overflow-hidden mb-4 max-h-52 overflow-y-auto">
                  {candidates.map((c, i) => (
                    <button
                      key={c.id}
                      onClick={() => { setSelected(c); setSearch(c.nickname ?? c.username ?? ''); setCandidates([]); }}
                      className={`w-full flex items-center gap-3 px-4 py-3 text-left cursor-pointer transition-colors hover:bg-sky-50 ${
                        i < candidates.length - 1 ? 'border-b border-gray-50' : ''
                      } ${selected?.id === c.id ? 'bg-sky-50' : ''}`}
                    >
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-rose-400 to-orange-300 flex items-center justify-center flex-shrink-0 overflow-hidden">
                        {c.avatar_url ? (
                          <img src={c.avatar_url} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-white text-xs font-bold">{avatarChar(c)}</span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-800">{c.nickname ?? c.username}</p>
                        <p className="text-xs text-gray-400">{c.phone ?? `@${c.username}`}</p>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${levelColor[c.level] ?? 'bg-gray-100 text-gray-500'}`}>
                        {c.level}
                      </span>
                      {selected?.id === c.id && (
                        <i className="ri-check-line text-sky-500 text-base flex-shrink-0" />
                      )}
                    </button>
                  ))}
                </div>
              )}

              {/* 已选中展示 */}
              {selected && (
                <div className="bg-sky-50 border border-sky-100 rounded-xl px-4 py-3 mb-4 flex items-center gap-3">
                  <i className="ri-checkbox-circle-fill text-sky-500 text-lg flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-sky-700">已选择：{selected.nickname ?? selected.username}</p>
                    <p className="text-xs text-sky-500">{selected.phone ?? `@${selected.username}`} · {selected.level}</p>
                  </div>
                  <button
                    onClick={() => { setSelected(undefined as unknown as Member); setSearch(''); }}
                    className="text-sky-400 hover:text-sky-600 cursor-pointer"
                  >
                    <i className="ri-close-line text-base" />
                  </button>
                </div>
              )}

              {/* 清除上级选项 */}
              {currentReferrer && (
                <button
                  onClick={() => setSelected(null)}
                  className={`w-full flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm mb-4 cursor-pointer transition-colors border ${
                    selected === null
                      ? 'border-rose-300 bg-rose-50 text-rose-600'
                      : 'border-gray-200 text-gray-500 hover:border-rose-200 hover:text-rose-500'
                  }`}
                >
                  <i className="ri-link-unlink-m text-base" />
                  <span>清除上级绑定（设为无上级）</span>
                  {selected === null && <i className="ri-check-line ml-auto text-rose-500" />}
                </button>
              )}

              {/* 提示 */}
              <div className="bg-amber-50 rounded-xl px-4 py-3 mb-5 flex items-start gap-2">
                <i className="ri-information-line text-amber-500 mt-0.5 flex-shrink-0 text-sm" />
                <p className="text-xs text-amber-700 leading-relaxed">
                  修改上级关系后，该会员将出现在新上级的团队列表中。历史订单业绩不会重新计算，仅影响后续数据统计。
                </p>
              </div>

              {/* 按钮 */}
              <div className="flex gap-3">
                <button
                  onClick={onClose}
                  disabled={submitting}
                  className="flex-1 border border-gray-200 text-gray-600 py-2.5 rounded-xl text-sm cursor-pointer whitespace-nowrap hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  取消
                </button>
                <button
                  onClick={handleSave}
                  disabled={!canSave}
                  className="flex-1 bg-sky-500 text-white py-2.5 rounded-xl text-sm font-medium cursor-pointer whitespace-nowrap hover:bg-sky-600 transition-colors disabled:opacity-50"
                >
                  {submitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      保存中...
                    </span>
                  ) : (
                    <span><i className="ri-save-line mr-1" />确认保存</span>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default SetReferrerModal;
